import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Paperclip, UploadCloud, X } from 'lucide-react';
import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';

import { formatFileSize } from '@/shared/utils/format-file-size.js';
import { SOLICITATION_EVIDENCE_ALLOWED_EXTENSIONS, type SolicitationEvidenceRejection, validateSolicitationEvidenceFiles } from '@/shared/utils/solicitation-evidence.js';

const ACCEPT = SOLICITATION_EVIDENCE_ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',');

interface SolicitationAttachmentsFieldLabels {
    attachments: string;
    attachmentsHint: string;
    addFiles: string;
    invalidFileFormat: (files: string) => string;
    fileTooLarge: (files: string) => string;
    tooManyFiles: string;
}

export interface SolicitationAttachmentsFieldProps {
    files: File[];
    onFilesChange: (files: File[]) => void;
    disabled?: boolean;
    labels: SolicitationAttachmentsFieldLabels;
}

export function SolicitationAttachmentsField({ files, onFilesChange, disabled = false, labels }: SolicitationAttachmentsFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [rejections, setRejections] = useState<SolicitationEvidenceRejection[]>([]);

    const openFilePicker = () => {
        if (!disabled) {
            inputRef.current?.click();
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFilePicker();
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (selected.length === 0) {
            return;
        }
        const result = validateSolicitationEvidenceFiles(selected, files.length);
        setRejections(result.rejected);
        if (result.accepted.length > 0) {
            onFilesChange([...files, ...result.accepted]);
        }
    };

    const handleRemove = (index: number) => {
        onFilesChange(files.filter((_, current) => current !== index));
    };

    const invalidFormatNames = rejections.filter((rejection) => rejection.reason === 'FORMAT').map((rejection) => rejection.file.name);
    const tooLargeNames = rejections.filter((rejection) => rejection.reason === 'SIZE').map((rejection) => rejection.file.name);
    const hasCountRejection = rejections.some((rejection) => rejection.reason === 'COUNT');

    const errorMessages: string[] = [];
    if (invalidFormatNames.length > 0) {
        errorMessages.push(labels.invalidFileFormat(invalidFormatNames.join(', ')));
    }
    if (tooLargeNames.length > 0) {
        errorMessages.push(labels.fileTooLarge(tooLargeNames.join(', ')));
    }
    if (hasCountRejection) {
        errorMessages.push(labels.tooManyFiles);
    }

    return (
        <Box data-testid="solicitation-attachments-field">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {labels.attachments}
            </Typography>
            <Box
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onClick={openFilePicker}
                onKeyDown={handleKeyDown}
                sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    bgcolor: 'background.paper',
                    ...(!disabled && { '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' } })
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    disabled={disabled}
                    data-testid="solicitation-attachments-input"
                    style={{ display: 'none' }}
                    onChange={handleChange}
                />
                <UploadCloud size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                    {labels.addFiles}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                    {labels.attachmentsHint}
                </Typography>
            </Box>
            {errorMessages.length > 0 && (
                <Box data-testid="solicitation-attachments-errors" sx={{ mt: 0.5 }}>
                    {errorMessages.map((message) => (
                        <Typography key={message} variant="caption" color="error" sx={{ display: 'block' }}>
                            {message}
                        </Typography>
                    ))}
                </Box>
            )}
            {files.map((file, index) => (
                <Box
                    key={`${file.name}-${index}`}
                    data-testid={`solicitation-attachments-item-${index}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                    <Paperclip size={16} />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                    </Typography>
                    <IconButton size="small" disabled={disabled} data-testid={`solicitation-attachments-remove-${index}`} onClick={() => handleRemove(index)}>
                        <X size={16} />
                    </IconButton>
                </Box>
            ))}
        </Box>
    );
}
